import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [checking, setChecking] = useState(true);
  const [validity, setValidity] = useState(null); // { valid: bool, reason?: str, email?: str }
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const check = async () => {
      if (!token) { setValidity({ valid: false, reason: 'not_found' }); setChecking(false); return; }
      try {
        const res = await axios.get(`${API}/auth/reset-password/validate`, { params: { token } });
        setValidity(res.data);
      } catch {
        setValidity({ valid: false, reason: 'not_found' });
      } finally { setChecking(false); }
    };
    check();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPw });
      setDone(true);
      toast.success('Password updated. Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally { setSubmitting(false); }
  };

  const friendlyReason = {
    not_found: 'This reset link is invalid or has been consumed. Request a new one from the forgot-password page.',
    already_used: 'This link has already been used. For security, each link works only once.',
    expired: 'This link has expired (links are valid for 60 minutes). Request a new one.',
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-[#4B5563] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>
        <Card className="border-[#E5E7EB]" data-testid="reset-password-card">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#002FA7]" />
              Set a new password
            </CardTitle>
          </CardHeader>
          <CardContent>
            {checking ? (
              <div className="py-8 text-center" data-testid="reset-checking">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#9CA3AF]" />
                <p className="text-sm text-[#4B5563] mt-3">Checking link...</p>
              </div>
            ) : done ? (
              <div className="text-center py-4" data-testid="reset-success">
                <div className="w-14 h-14 rounded-full bg-[#34C759]/15 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-[#34C759]" />
                </div>
                <p className="text-[#0A0A0A] font-medium">Password updated</p>
                <p className="text-sm text-[#4B5563] mt-2">Redirecting you to login...</p>
              </div>
            ) : !validity?.valid ? (
              <div className="text-center py-4" data-testid="reset-invalid">
                <div className="w-14 h-14 rounded-full bg-[#FF3B30]/15 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-7 h-7 text-[#FF3B30]" />
                </div>
                <p className="text-[#0A0A0A] font-medium mb-2">Link not valid</p>
                <p className="text-sm text-[#4B5563] mb-6">
                  {friendlyReason[validity?.reason] || 'This reset link cannot be used.'}
                </p>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full bg-[#002FA7] hover:bg-[#00227A]"
                  data-testid="request-new-link-btn"
                >
                  Request a new link
                </Button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <p className="text-sm text-[#4B5563] mb-4">
                  Choose a new password for <strong>{validity.email}</strong>. You'll be logged out of other sessions after this.
                </p>
                <Label className="text-xs">New password (min 8)</Label>
                <Input
                  type="password"
                  value={newPw}
                  onChange={e => setNewPw(e.target.value)}
                  autoFocus
                  required
                  minLength={8}
                  className="mt-1 mb-3"
                  data-testid="reset-new-password-input"
                />
                <Label className="text-xs">Confirm new password</Label>
                <Input
                  type="password"
                  value={confirmPw}
                  onChange={e => setConfirmPw(e.target.value)}
                  required
                  minLength={8}
                  className="mt-1 mb-4"
                  data-testid="reset-confirm-password-input"
                />
                <Button
                  type="submit"
                  className="w-full bg-[#002FA7] hover:bg-[#00227A]"
                  disabled={submitting || !newPw || !confirmPw}
                  data-testid="reset-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Update password
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
